import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconHourglass,
  IconLogout,
  IconMinimize,
  IconMusic,
  IconPlayerPlay,
  IconPlayerSkipForward,
  IconPlus,
  IconX,
} from '@tabler/icons-react';
import toast from 'react-hot-toast';
import { Modal } from '@mantine/core';
import { Carousel } from '@mantine/carousel';

import HeartEkgIcon from '@components/Athlete/HeartEkgIcon';
import {
  AppleMusicMiniPlayer,
} from '@components/Athlete/AppleMusicPlayer';
import AddExerciseSheet from '@components/Athlete/AddExerciseSheet';
import { NewAwardsList } from '@components/Athlete/TrophyCase';
import appStore from '@duck/app-store';
import useInterval from '@util/useInterval';
import { toErrorMessage } from '@util/toErrorMessage';
import {
  createHeartRateMonitor,
  isHeartRateBluetoothSupported,
} from '@util/heartRateMonitor';
import {
  addExerciseToWorkoutSession,
  completeSetAttempt,
  completeWorkoutSession,
  endRestAttempt,
  extraRestSetAttempt,
  fetchAthleteCalendar,
  fetchAthleteHome,
  fetchStartableWorkouts,
  heartbeatWorkoutSession,
  isSessionCompleteResponse,
  loadWorkout,
  mapLoadedWorkout,
  postHeartRateSamples,
  remainingRestSeconds,
  restRemainingFromEndsAt,
  restTotalFromWindow,
  sessionHasOpenSets,
  skipSetAttempt,
  skipWorkoutSlot,
  startCustomWorkoutSession,
  startOverlaySession,
  startRestAttempt,
  startSetAttempt,
  startSlotSession,
  toWholeLb,
  unskipSetAttempt,
} from '@util/athleteSession';

import DayActivityModal, {
  entriesForDay,
  ymd,
} from './DayActivityModal';
import homeClasses from './Home.module.css';
import classes from './Workout.module.css';

const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function buildCalendarCells(year, month, highlightedDay, workoutDays, overlayDays = []) {
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  const overlaySet = new Set(overlayDays);

  for (let i = 0; i < startPad; i += 1) {
    cells.push({ key: `pad-${i}`, label: '', muted: true });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({
      key: `day-${day}`,
      day,
      label: String(day),
      muted: false,
      today: day === highlightedDay,
      workout: workoutDays.includes(day),
      overlay: overlaySet.has(day),
    });
  }

  return cells;
}

function formatClock(totalSeconds) {
  const safe = Math.max(0, totalSeconds || 0);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatNow() {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
    .format(new Date())
    .toUpperCase()
    .replace(',', ' |');
}

function firstOpenSetIndex(sets) {
  const index = sets.findIndex((set) => !set.completed);
  return index === -1 ? Math.max(sets.length - 1, 0) : index;
}

function prescriptionLabel(exercise) {
  const sets = exercise.planned_sets || [];
  if (!sets.length) return '';
  const first = sets[0];
  if (
    first.prescribed_how &&
    first.prescribed_weight == null &&
    first.prescribed_reps == null
  ) {
    return first.prescribed_how;
  }
  const count = sets.length;
  const reps = first.prescribed_reps;
  const how = first.prescribed_how ? ` ${first.prescribed_how}` : '';
  if (reps == null) return `${count} sets`;
  return `${count} × ${reps}${how}`;
}

function prescriptionParts(exercise) {
  const sets = exercise?.planned_sets || [];
  if (!sets.length) {
    return {
      sets: null,
      reps: null,
      weight: null,
      style: null,
      rest: null,
    };
  }

  const first = sets[0];
  let how = (first.prescribed_how || '').trim();
  let rest = null;
  const restMatch = how.match(/\s*[·•]\s*(rest\b.*)$/i);
  if (restMatch) {
    rest = restMatch[1].replace(/^rest\s+/i, '').trim();
    how = how.slice(0, restMatch.index).trim();
  } else if (/^rest\b/i.test(how)) {
    rest = how.replace(/^rest\s+/i, '').trim();
    how = '';
  }

  const weight =
    first.prescribed_weight == null
      ? null
      : toWholeLb(first.prescribed_weight);

  return {
    sets: sets.length,
    reps: first.prescribed_reps == null ? null : first.prescribed_reps,
    weight,
    style: how || null,
    rest: rest || null,
  };
}

function youtubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname.startsWith('/embed/')) {
        return `https://www.youtube.com/embed/${parsed.pathname.split('/')[2]}`;
      }
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'avif',
  'bmp',
]);

function isImageUrl(url) {
  if (!url) return false;
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    const extension = pathname.split('.').pop();
    return IMAGE_EXTENSIONS.has(extension);
  } catch {
    return false;
  }
}

function demoImages(assets = []) {
  const images = [];
  assets.forEach((asset) => {
    if (
      (asset.variety_formatted === 'image' || Number(asset.variety) === 1) &&
      asset.filename_url
    ) {
      images.push({
        key: asset.id || asset.filename_url,
        src: asset.filename_url,
      });
      return;
    }
    if (
      asset.external_link_url &&
      isImageUrl(asset.external_link_url) &&
      !youtubeEmbedUrl(asset.external_link_url)
    ) {
      images.push({
        key: asset.id || asset.external_link_url,
        src: asset.external_link_url,
      });
    }
  });
  return images;
}

function pickDemoLink(assets = []) {
  return (
    assets.find((asset) => {
      if (
        !(
          asset.variety_formatted === 'external_link' ||
          Number(asset.variety) === 3
        ) ||
        !asset.external_link_url
      ) {
        return false;
      }
      return !isImageUrl(asset.external_link_url);
    }) || null
  );
}

function youtubeSearchQuery(exerciseName, tagsByVariety) {
  const tags = tagsByVariety?.youtube_search || [];
  const extras = tags.filter((tag) => tag !== exerciseName);
  return [exerciseName, ...extras].filter(Boolean).join(' ');
}

async function searchYoutubeVideos(query) {
  const key = import.meta.env.VITE_YOUTUBE_API_KEY;
  if (!key) {
    throw new Error('YouTube API key is not configured');
  }
  const params = new URLSearchParams({
    part: 'snippet',
    type: 'video',
    videoDuration: 'short',
    maxResults: '10',
    q: `${query} how to shorts`.trim(),
    key,
  });
  const response = await fetch(
    `https://www.googleapis.com/youtube/v3/search?${params.toString()}`
  );
  if (!response.ok) {
    throw new Error('YouTube search failed');
  }
  const data = await response.json();
  return (data.items || [])
    .map((item) => {
      const videoId = item?.id?.videoId;
      if (!videoId) return null;
      const thumb =
        item.snippet?.thumbnails?.medium?.url ||
        item.snippet?.thumbnails?.default?.url ||
        '';
      return {
        videoId,
        title: item.snippet?.title || 'Video',
        channelTitle: item.snippet?.channelTitle || '',
        thumbnailUrl: thumb,
      };
    })
    .filter(Boolean);
}

function ExerciseDemo({ assets, exerciseName, tagsByVariety }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const images = demoImages(assets);
  const searchQuery = youtubeSearchQuery(exerciseName, tagsByVariety);
  const canSearch = Boolean(searchQuery);

  const openYoutubeSearch = async () => {
    if (!canSearch) return;
    setSelectedVideoId(null);
    setVideoModalOpen(true);
    if (previews.length) return;
    setSearching(true);
    try {
      const results = await searchYoutubeVideos(searchQuery);
      setPreviews(results);
      if (!results.length) {
        toast.error('No videos found for this exercise');
      }
    } catch (error) {
      toast.error(toErrorMessage(error) || 'YouTube search failed');
      setVideoModalOpen(false);
    } finally {
      setSearching(false);
    }
  };

  const closeYoutubeModal = () => {
    setVideoModalOpen(false);
    setSelectedVideoId(null);
  };

  const youtubeModal = canSearch ? (
    <Modal
      opened={videoModalOpen}
      onClose={closeYoutubeModal}
      centered
      size="lg"
      radius="md"
      padding="md"
      title={null}
      withCloseButton
      classNames={{
        content: classes.demoLightboxContent,
        body: classes.demoLightboxBody,
        header: classes.demoLightboxHeader,
      }}
    >
      {searching ? (
        <p className={classes.demoVideoStatus}>Searching YouTube…</p>
      ) : selectedVideoId ? (
        <div className={classes.demoVideoPlayerWrap}>
          <iframe
            className={classes.demoVideoPlayer}
            src={`https://www.youtube.com/embed/${selectedVideoId}?autoplay=1`}
            title="Exercise demo video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
          <button
            type="button"
            className={classes.demoVideoBack}
            onClick={() => setSelectedVideoId(null)}
          >
            Back to results
          </button>
        </div>
      ) : previews.length ? (
        <>
          <Carousel
            withIndicators={previews.length > 1}
            withControls={previews.length > 1}
            loop={previews.length > 1}
            height="100%"
            classNames={{
              root: classes.demoLightboxCarousel,
              viewport: classes.demoLightboxViewport,
              container: classes.demoLightboxContainer,
              slide: classes.demoLightboxSlide,
              control: classes.demoCarouselControl,
              indicators: classes.demoCarouselIndicators,
              indicator: classes.demoCarouselIndicator,
            }}
          >
            {previews.map((preview) => (
              <Carousel.Slide key={preview.videoId}>
                <button
                  type="button"
                  className={classes.demoVideoPreview}
                  onClick={() => setSelectedVideoId(preview.videoId)}
                  aria-label={`Play ${preview.title}`}
                >
                  {preview.thumbnailUrl ? (
                    <img
                      src={preview.thumbnailUrl}
                      alt=""
                      className={classes.demoVideoPreviewImage}
                    />
                  ) : null}
                  <span className={classes.demoVideoPreviewPlay}>
                    <IconPlayerPlay size={28} />
                  </span>
                  <span className={classes.demoVideoPreviewMeta}>
                    <span className={classes.demoVideoPreviewTitle}>
                      {preview.title}
                    </span>
                    {preview.channelTitle ? (
                      <span className={classes.demoVideoPreviewChannel}>
                        {preview.channelTitle}
                      </span>
                    ) : null}
                  </span>
                </button>
              </Carousel.Slide>
            ))}
          </Carousel>
          <p className={classes.demoThumbsHint}>
            Swipe for more · tap to play
          </p>
        </>
      ) : (
        <p className={classes.demoVideoStatus}>No videos found</p>
      )}
    </Modal>
  ) : null;

  const findVideosControl = canSearch ? (
    <button
      type="button"
      className={classes.demoFindVideos}
      onClick={openYoutubeSearch}
    >
      Find videos
    </button>
  ) : null;

  if (images.length) {
    const multi = images.length > 1;
    return (
      <>
        <div className={classes.demoThumbs}>
          <div className={classes.demoThumbRow}>
            {images.map((image, index) => (
              <button
                key={image.key}
                type="button"
                className={classes.demoThumb}
                onClick={() => {
                  setStartIndex(index);
                  setLightboxOpen(true);
                }}
                aria-label={
                  multi
                    ? `View exercise photo ${index + 1} of ${images.length}`
                    : 'View exercise photo'
                }
              >
                <img src={image.src} alt="" className={classes.demoThumbImage} />
              </button>
            ))}
          </div>
          <p className={classes.demoThumbsHint}>
            {multi ? `Tap a photo · ${images.length} views` : 'Tap to enlarge'}
          </p>
          {findVideosControl}
        </div>
        <Modal
          opened={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          centered
          size="lg"
          radius="md"
          padding="md"
          title={null}
          withCloseButton
          classNames={{
            content: classes.demoLightboxContent,
            body: classes.demoLightboxBody,
            header: classes.demoLightboxHeader,
          }}
        >
          <Carousel
            key={`${startIndex}-${lightboxOpen}`}
            initialSlide={startIndex}
            withIndicators={multi}
            withControls={multi}
            loop={multi}
            height="100%"
            classNames={{
              root: classes.demoLightboxCarousel,
              viewport: classes.demoLightboxViewport,
              container: classes.demoLightboxContainer,
              slide: classes.demoLightboxSlide,
              control: classes.demoCarouselControl,
              indicators: classes.demoCarouselIndicators,
              indicator: classes.demoCarouselIndicator,
            }}
          >
            {images.map((image) => (
              <Carousel.Slide key={image.key}>
                <img
                  className={classes.demoLightboxImage}
                  src={image.src}
                  alt=""
                />
              </Carousel.Slide>
            ))}
          </Carousel>
        </Modal>
        {youtubeModal}
      </>
    );
  }

  const link = pickDemoLink(assets);
  if (link?.external_link_url) {
    const embed = youtubeEmbedUrl(link.external_link_url);
    if (embed) {
      return (
        <>
          <div className={classes.demo}>
            <iframe
              className={classes.demoEmbed}
              src={embed}
              title="Exercise demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          {findVideosControl}
          {youtubeModal}
        </>
      );
    }
    return (
      <>
        <div className={classes.demo}>
          <div className={classes.demoInner}>
            <a
              className={classes.demoLink}
              href={link.external_link_url}
              target="_blank"
              rel="noreferrer"
            >
              <div className={classes.demoPlay}>
                <IconPlayerPlay size={26} />
              </div>
              <p className={classes.demoText}>Open demo</p>
            </a>
          </div>
        </div>
        {findVideosControl}
        {youtubeModal}
      </>
    );
  }

  if (canSearch) {
    return (
      <>
        <button
          type="button"
          className={classes.demo}
          onClick={openYoutubeSearch}
          aria-label="Find exercise videos on YouTube"
        >
          <div className={classes.demoInner}>
            <div className={classes.demoPlay}>
              <IconPlayerPlay size={26} />
            </div>
            <p className={classes.demoText}>Find videos</p>
          </div>
        </button>
        {youtubeModal}
      </>
    );
  }

  return (
    <div className={classes.demo}>
      <div className={classes.demoInner}>
        <div className={classes.demoPlay}>
          <IconPlayerPlay size={26} />
        </div>
        <p className={classes.demoText}>Stronger Athletes, Better People</p>
      </div>
    </div>
  );
}

function exerciseStatus(exercise) {
  const sets = exercise.planned_sets || [];
  if (!sets.length) return 'upcoming';
  if (sets.every((set) => set.completed)) return 'completed';
  if (sets.some((set) => set.completed)) return 'current';
  return 'upcoming';
}

function breadcrumbParts(programName, sessionLabel) {
  const parts = [programName];
  String(sessionLabel || '')
    .split('|')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => parts.push(part));
  return parts;
}

function applyFocusFromResponse(nextSession, currentWorkoutExerciseId) {
  if (!currentWorkoutExerciseId) return null;
  const nextIdx = nextSession.exercises.findIndex(
    (item) => item.id === currentWorkoutExerciseId,
  );
  return nextIdx >= 0 ? nextIdx : null;
}

function findNextOpenAfter(nextSession, exIdx, setIdx) {
  const sets = nextSession.exercises[exIdx]?.planned_sets || [];
  const nextOpen = sets.findIndex(
    (set, index) => index > setIdx && !set.completed,
  );
  if (nextOpen >= 0) {
    return { exerciseIndex: exIdx, setIndex: nextOpen };
  }
  const nextEx = nextSession.exercises.findIndex(
    (item, index) =>
      index > exIdx && (item.planned_sets || []).some((set) => !set.completed),
  );
  if (nextEx >= 0) {
    return {
      exerciseIndex: nextEx,
      setIndex: firstOpenSetIndex(
        nextSession.exercises[nextEx].planned_sets || [],
      ),
    };
  }
  return null;
}

function buildRestModalContext({
  exerciseName,
  setIndex: completedSetIndex,
  setCount,
  completedSet,
  nextExercise,
  nextSet,
}) {
  const weight = toWholeLb(
    completedSet?.actual_weight ?? completedSet?.prescribed_weight,
  );
  const reps = completedSet?.actual_reps ?? completedSet?.prescribed_reps;
  const rpeVal = completedSet?.rpe;
  const metaParts = [`Set ${completedSetIndex + 1} of ${setCount}`];
  if (weight != null && reps != null) {
    metaParts.push(`${weight} lb × ${reps}`);
  } else if (completedSet?.prescribed_how) {
    metaParts.push(completedSet.prescribed_how);
  }
  if (rpeVal) metaParts.push(`RPE ${rpeVal}`);

  let nextLabel = 'Session complete';
  let nextSub = '';
  if (nextSet) {
    const nextWeight = toWholeLb(nextSet.prescribed_weight);
    const nextReps = nextSet.prescribed_reps;
    if (nextWeight != null && nextReps != null) {
      nextLabel = `Next Set ${nextWeight} lb × ${nextReps}`;
      nextSub =
        weight != null && nextWeight === weight && nextReps === reps
          ? 'Same weight'
          : nextExercise?.name || '';
    } else {
      nextLabel = nextSet.prescribed_how || 'Next set';
      nextSub = nextExercise?.name || '';
    }
  }

  return {
    exerciseName: exerciseName || 'Exercise',
    setMeta: metaParts.join(' | '),
    nextLabel,
    nextSub,
  };
}

export default function AthleteWorkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const athlete = appStore((state) => state.athlete);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [home, setHome] = useState(null);
  const [profile, setProfile] = useState(null);
  const [phase, setPhase] = useState('rest'); // picker | loaded | active | completed | rest | customEmpty
  const [startable, setStartable] = useState([]);
  const [newAwards, setNewAwards] = useState([]);
  const [session, setSession] = useState(null);
  const [addExerciseOpen, setAddExerciseOpen] = useState(false);
  const [addingExercise, setAddingExercise] = useState(false);
  const [endCustomConfirmOpen, setEndCustomConfirmOpen] = useState(false);
  const [customContinueOpen, setCustomContinueOpen] = useState(false);
  const [endingCustom, setEndingCustom] = useState(false);
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [setIndex, setSetIndex] = useState(0);
  const [cueTab, setCueTab] = useState('key_points');
  const [weight, setWeight] = useState(0);
  const [reps, setReps] = useState(0);
  const [rpe, setRpe] = useState(8);
  const [restSeconds, setRestSeconds] = useState(0);
  const [restTotal, setRestTotal] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [volume, setVolume] = useState(0);
  const [remaining, setRemaining] = useState(0);
  const [busy, setBusy] = useState(false);
  const [activeAttemptId, setActiveAttemptId] = useState(null);
  const [setStartedAt, setSetStartedAt] = useState(null);
  const [setClockSeconds, setSetClockSeconds] = useState(0);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [restAttemptId, setRestAttemptId] = useState(null);
  const [restModalMode, setRestModalMode] = useState('hidden'); // hidden | expanded | minimized
  const [restEndingFlash, setRestEndingFlash] = useState(false);
  const [restModalContext, setRestModalContext] = useState(null);
  const [editingSet, setEditingSet] = useState(false);
  const [adjustmentsOpen, setAdjustmentsOpen] = useState(true);
  const [skipSetConfirmOpen, setSkipSetConfirmOpen] = useState(false);
  const [unskipSetConfirmOpen, setUnskipSetConfirmOpen] = useState(false);
  const [skippingWorkout, setSkippingWorkout] = useState(false);
  const [starting, setStarting] = useState(false);
  const [modalDate, setModalDate] = useState(null);
  const [modalEntries, setModalEntries] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [hrBpm, setHrBpm] = useState(null);
  const [hrStatus, setHrStatus] = useState(() =>
    isHeartRateBluetoothSupported() ? 'disconnected' : 'unsupported',
  );
  const [musicDockHidden, setMusicDockHidden] = useState(false);
  const modalRequestRef = useRef(0);
  const pendingRestRestoreRef = useRef(null);
  const restWasActiveRef = useRef(false);
  const restGoActiveRef = useRef(false);
  const hrBufferRef = useRef([]);
  const hrMonitorRef = useRef(null);
  const hrFlushingRef = useRef(false);
  const hrSessionIdRef = useRef(null);

  function clearRestUi() {
    setRestSeconds(0);
    setRestTotal(0);
    setRestAttemptId(null);
    setRestModalMode('hidden');
    setRestEndingFlash(false);
    setRestModalContext(null);
    restWasActiveRef.current = false;
    restGoActiveRef.current = false;
  }

  function clearSessionState() {
    setSession(null);
    setExerciseIndex(0);
    setSetIndex(0);
    setActiveAttemptId(null);
    setSetStartedAt(null);
    setSetClockSeconds(0);
    setCompleteModalOpen(false);
    pendingRestRestoreRef.current = null;
    setEditingSet(false);
    clearRestUi();
    setVolume(0);
    setElapsed(0);
    setRemaining(0);
    setNewAwards([]);
  }

  async function flushHeartRateSamples() {
    const sessionId = hrSessionIdRef.current;
    const batch = hrBufferRef.current.slice();
    if (!sessionId || !batch.length || hrFlushingRef.current) return false;
    hrFlushingRef.current = true;
    try {
      await postHeartRateSamples(
        sessionId,
        batch.map((sample) => ({
          recorded_at: sample.recorded_at,
          bpm: sample.bpm,
        })),
      );
      const sent = new Set(batch);
      hrBufferRef.current = hrBufferRef.current.filter(
        (sample) => !sent.has(sample),
      );
      return true;
    } catch {
      return false;
    } finally {
      hrFlushingRef.current = false;
    }
  }

  function ensureHeartRateMonitor() {
    if (hrMonitorRef.current) return hrMonitorRef.current;
    hrMonitorRef.current = createHeartRateMonitor({
      onSample: ({ bpm, recordedAt }) => {
        setHrBpm(bpm);
        hrBufferRef.current.push({
          recorded_at: recordedAt,
          bpm,
        });
      },
      onStatus: (next) => {
        setHrStatus(next);
      },
    });
    return hrMonitorRef.current;
  }

  async function onConnectHeartRate() {
    if (hrStatus === 'unsupported' || hrStatus === 'connecting') return;
    try {
      // Reuses a previously permitted strap when possible; otherwise opens Chrome's picker.
      await ensureHeartRateMonitor().connect();
    } catch (error) {
      if (error?.name === 'NotFoundError') return;
      toast.error(toErrorMessage(error, 'Could not connect heart rate strap'));
    }
  }

  async function onDisconnectHeartRate() {
    await flushHeartRateSamples();
    if (hrMonitorRef.current) {
      await hrMonitorRef.current.disconnect();
    }
    setHrBpm(null);
  }

  async function leaveWorkout() {
    await onDisconnectHeartRate();
    navigate('/');
  }

  function enterSessionCompleted(stats, awards = []) {
    void flushHeartRateSamples();
    applySessionStats(stats);
    setRemaining(0);
    clearRestUi();
    setCompleteModalOpen(false);
    setSetStartedAt(null);
    setSetClockSeconds(0);
    setEditingSet(false);
    setActiveAttemptId(null);
    pendingRestRestoreRef.current = null;
    if (Array.isArray(awards) && awards.length) {
      setNewAwards(awards);
    }
    setPhase('completed');
    fetchAthleteHome()
      .then((summary) => {
        setHome(summary || null);
        if (summary?.profile) setProfile(summary.profile);
      })
      .catch(() => {});
  }

  function applyRestFromAttempt(attemptLike) {
    const remainingRest = restRemainingFromEndsAt(attemptLike?.rest_ends_at);
    const total = restTotalFromWindow(
      attemptLike?.rest_started_at,
      attemptLike?.rest_ends_at,
    );
    if (remainingRest <= 0) {
      clearRestUi();
      return false;
    }
    setRestTotal(total || remainingRest);
    setRestSeconds(remainingRest);
    setRestAttemptId(attemptLike.set_attempt_id || attemptLike.id || null);
    restWasActiveRef.current = true;
    setRestModalMode('expanded');
    setRestEndingFlash(false);
    return true;
  }

  function findActiveRestContext(sessionData) {
    const exercises = sessionData?.exercises || [];
    for (let exIdx = 0; exIdx < exercises.length; exIdx += 1) {
      const sets = exercises[exIdx].planned_sets || [];
      for (let setIdx = 0; setIdx < sets.length; setIdx += 1) {
        const set = sets[setIdx];
        if (restRemainingFromEndsAt(set.rest_ends_at) <= 0) continue;
        return { exerciseIndex: exIdx, setIndex: setIdx, set };
      }
    }
    return null;
  }

  function applyLoadedPayload(nextHome, payload) {
    setHome(nextHome || null);
    setProfile(nextHome?.profile || null);
    if (!payload?.workout) {
      clearSessionState();
      setPhase('rest');
      return;
    }

    const mapped = mapLoadedWorkout(payload, nextHome);
    let nextExerciseIndex = mapped.exerciseIndex;
    let nextSetIndex = firstOpenSetIndex(
      mapped.session.exercises[nextExerciseIndex]?.planned_sets || [],
    );
    let resumeSet =
      mapped.session.exercises[nextExerciseIndex]?.planned_sets?.[nextSetIndex] ||
      null;
    let restContextMeta = null;

    const restContext = findActiveRestContext(mapped.session);
    if (restContext) {
      const restingSet = restContext.set;
      if (!restingSet.completed && restingSet.outcome === 1) {
        nextExerciseIndex = restContext.exerciseIndex;
        nextSetIndex = restContext.setIndex;
        resumeSet = restingSet;
      } else {
        nextExerciseIndex = restContext.exerciseIndex;
        const sets =
          mapped.session.exercises[nextExerciseIndex]?.planned_sets || [];
        const nextOpen = sets.findIndex(
          (set, index) => index > restContext.setIndex && !set.completed,
        );
        if (nextOpen >= 0) {
          nextSetIndex = nextOpen;
          resumeSet = sets[nextOpen];
        } else {
          const nextEx = mapped.session.exercises.findIndex(
            (item, index) =>
              index > restContext.exerciseIndex &&
              (item.planned_sets || []).some((set) => !set.completed),
          );
          if (nextEx >= 0) {
            nextExerciseIndex = nextEx;
            nextSetIndex = firstOpenSetIndex(
              mapped.session.exercises[nextEx].planned_sets || [],
            );
            resumeSet =
              mapped.session.exercises[nextEx].planned_sets[nextSetIndex];
          }
        }
      }

      const restingExercise =
        mapped.session.exercises[restContext.exerciseIndex];
      const nextExercise =
        mapped.session.exercises[nextExerciseIndex] || restingExercise;
      restContextMeta = buildRestModalContext({
        exerciseName: restingExercise?.name,
        setIndex: restContext.setIndex,
        setCount: (restingExercise?.planned_sets || []).length,
        completedSet: restingSet,
        nextExercise,
        nextSet: resumeSet,
      });
    }

    setSession(mapped.session);
    setExerciseIndex(nextExerciseIndex);
    setSetIndex(nextSetIndex);
    setVolume(mapped.session.session_stats.volume_lb);
    setElapsed(mapped.session.session_stats.elapsed_seconds);
    setRemaining(mapped.session.session_stats.remaining_seconds);
    setEditingSet(false);
    setCompleteModalOpen(false);
    clearRestUi();

    if (resumeSet) {
      syncSteppersFromSet(resumeSet);
      enterSetFromLoaded(resumeSet);
    } else {
      setActiveAttemptId(null);
      setSetStartedAt(null);
      setSetClockSeconds(0);
    }

    pendingRestRestoreRef.current = restContext
      ? { set: restContext.set, context: restContextMeta }
      : null;

    const hasOpenSession = Boolean(mapped.session.workout_session_id);
    const hasOpenSets = sessionHasOpenSets(mapped.session);
    const isCustom = mapped.session.source === 'custom';
    const exerciseCount = (mapped.session.exercises || []).length;
    const sessionCompleted =
      isSessionCompleteResponse(payload) ||
      mapped.session.session_status_formatted === 'completed' ||
      Number(mapped.session.session_status) === 2;

    if (sessionCompleted) {
      clearRestUi();
      pendingRestRestoreRef.current = null;
      setRemaining(0);
      setCustomContinueOpen(false);
      setPhase('completed');
    } else if (isCustom && exerciseCount === 0) {
      setCustomContinueOpen(false);
      setPhase('customEmpty');
    } else if (hasOpenSession && !hasOpenSets) {
      clearRestUi();
      pendingRestRestoreRef.current = null;
      setRemaining(0);
      if (isCustom) {
        setPhase('active');
        setCustomContinueOpen(true);
      } else {
        setCustomContinueOpen(false);
        setPhase('completed');
      }
    } else if (hasOpenSession && hasOpenSets) {
      setCustomContinueOpen(false);
      setPhase('active');
    } else {
      setCustomContinueOpen(false);
      setPhase('loaded');
    }
  }

  async function loadStartableItem(item, nextHome = home) {
    const payload = await loadWorkout({
      source: item.source,
      workoutSlotId: item.workout_slot_id,
      athleteCalendarEventId: item.athlete_calendar_event_id,
      workoutSessionId: item.workout_session_id,
      onDate: item.on_date,
    });
    applyLoadedPayload(nextHome, payload);
  }

  async function bootstrapCustomSession(nextHome, workoutSessionId = null) {
    let sessionId = workoutSessionId;
    if (!sessionId) {
      const started = await startCustomWorkoutSession();
      sessionId = started?.id;
    }
    if (!sessionId) {
      throw new Error('Custom workout session did not start');
    }
    const payload = await loadWorkout({
      source: 'custom',
      workoutSessionId: sessionId,
    });
    applyLoadedPayload(nextHome, payload);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);

    Promise.all([fetchAthleteHome(), fetchStartableWorkouts()])
      .then(async ([nextHome, startablePayload]) => {
        if (cancelled) return;
        setHome(nextHome || null);
        setProfile(nextHome?.profile || null);
        const items = startablePayload.data || [];
        setStartable(items);

        const deep = location.state || {};
        let target = null;
        if (deep.source === 'custom') {
          await bootstrapCustomSession(
            nextHome,
            deep.workoutSessionId || deep.id || null,
          );
          return;
        }
        if (deep.source === 'slot' && deep.id) {
          target = items.find(
            (item) =>
              item.source === 'slot' && String(item.workout_slot_id) === String(deep.id),
          ) || {
            source: 'slot',
            workout_slot_id: deep.id,
            on_date: startablePayload.on_date,
          };
        } else if (deep.source === 'overlay' && deep.id) {
          target = items.find(
            (item) =>
              item.source === 'overlay' &&
              String(item.athlete_calendar_event_id) === String(deep.id),
          ) || {
            source: 'overlay',
            athlete_calendar_event_id: deep.id,
            on_date: deep.onDate || startablePayload.on_date,
          };
        } else if (nextHome?.open_workout_session?.id) {
          const open = nextHome.open_workout_session;
          if (open.source === 'custom') {
            await bootstrapCustomSession(
              nextHome,
              open.workout_session_id || open.id,
            );
            return;
          }
          if (open.source === 'overlay' && open.athlete_calendar_event_id) {
            target = {
              source: 'overlay',
              athlete_calendar_event_id: open.athlete_calendar_event_id,
              on_date: open.on_date || open.performed_on || startablePayload.on_date,
            };
          } else if (open.workout_slot_id) {
            target = {
              source: 'slot',
              workout_slot_id: open.workout_slot_id,
              on_date: startablePayload.on_date,
            };
          }
        } else if (items.length === 1) {
          target = items[0];
        }

        if (target) {
          await loadStartableItem(target, nextHome);
        } else if (items.length > 1) {
          clearSessionState();
          setPhase('picker');
        } else {
          clearSessionState();
          setPhase('rest');
        }
      })
      .catch((error) => {
        if (cancelled) return;
        const message = toErrorMessage(error);
        setLoadError(message);
        toast.error(message);
        setPhase('rest');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName =
    (athlete?.full_name && athlete.full_name.split(' ')[0]) ||
    profile?.first_name ||
    'Athlete';

  const isCustomSession = session?.source === 'custom';
  const hasPrimedSession = Boolean(session?.exercises?.length);
  const isCustomEmpty = phase === 'customEmpty' || (isCustomSession && !hasPrimedSession && phase !== 'completed');
  const isActive = phase === 'active' && hasPrimedSession;
  const isLoaded = phase === 'loaded' && hasPrimedSession;
  const isCompleted = phase === 'completed' && (hasPrimedSession || isCustomSession);
  const exercise = session?.exercises?.[exerciseIndex];
  const sets = exercise?.planned_sets || [];
  const activeSet = sets[setIndex];
  const nextExercise = (session?.exercises || [])
    .slice(exerciseIndex + 1)
    .find((item) => exerciseStatus(item) !== 'completed');

  const progressCount = hasPrimedSession ? exerciseIndex + 1 : 0;
  const finishedSetCount = hasPrimedSession
    ? session.exercises.reduce(
        (count, item) =>
          count +
          (item.planned_sets || []).filter((set) => set.completed).length,
        0,
      )
    : 0;
  const totalSetCount = hasPrimedSession
    ? session.exercises.reduce(
        (count, item) => count + (item.planned_sets || []).length,
        0,
      )
    : 0;
  const exerciseProgress = isCompleted
    ? 1
    : totalSetCount > 0
      ? finishedSetCount / totalSetCount
      : 0;
  const crumbs = hasPrimedSession
    ? breadcrumbParts(session?.program_name, session?.session_label)
    : phase === 'picker'
      ? ['Choose workout']
      : ['Rest Day'];
  const canSkipWorkout =
    isLoaded && session?.source === 'slot' && session?.workout_slot_id;
  const setFinished = Boolean(activeSet?.completed);
  const setSkipped = activeSet?.outcome === 3;
  const canEditCompletedSet = setFinished && !setSkipped;
  const setInProgress = Boolean(
    isActive && !setFinished && setStartedAt && !completeModalOpen,
  );
  const showSetTimerRail = setInProgress;
  const restUiActive =
    restModalMode !== 'hidden' && (restSeconds > 0 || restEndingFlash);
  const showRestTimerRail = restModalMode === 'minimized' && restUiActive;
  const showRestModal = restModalMode === 'expanded' && restUiActive;
  const restPhase = restSeconds > 10 ? 'ring' : 'countdown';
  const showOnCardAdjustments = canEditCompletedSet || editingSet;
  const canSkipSet =
    isActive && !setFinished && !setStartedAt && !completeModalOpen;
  const isResting =
    isActive &&
    !setFinished &&
    !setStartedAt &&
    !completeModalOpen &&
    restSeconds > 0;
  const steppersLocked = isActive && setFinished && !editingSet;
  const prescription = prescriptionParts(exercise);

  useEffect(() => {
    if (editingSet) {
      setAdjustmentsOpen(true);
      return;
    }
    if (setFinished && !setSkipped) {
      setAdjustmentsOpen(false);
    } else {
      setAdjustmentsOpen(true);
    }
  }, [setFinished, setSkipped, editingSet, setIndex, exerciseIndex]);

  const calendarCells = useMemo(() => {
    if (!home?.calendar) return [];
    return buildCalendarCells(
      home.calendar.year,
      home.calendar.month,
      home.calendar.highlighted_day,
      home.calendar.workout_days || [],
      home.calendar.overlay_days || [],
    );
  }, [home?.calendar]);

  const todayKey = useMemo(() => ymd(new Date()), []);
  const modalDateKey = modalDate ? ymd(modalDate) : null;
  const modalDateLabel = modalDate
    ? modalDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      })
    : '';
  const dayEntries = useMemo(
    () => (modalDateKey ? entriesForDay(modalEntries, modalDateKey) : []),
    [modalEntries, modalDateKey],
  );

  function openDayModal(day) {
    const calendar = home?.calendar;
    if (!calendar) return;
    const date = new Date(calendar.year, calendar.month, day);
    const requestId = modalRequestRef.current + 1;
    modalRequestRef.current = requestId;
    setModalDate(date);
    setModalEntries([]);
    setModalLoading(true);
    const fromDate = ymd(new Date(calendar.year, calendar.month, 1));
    const toDate = ymd(new Date(calendar.year, calendar.month + 1, 0));
    fetchAthleteCalendar({ fromDate, toDate })
      .then((payload) => {
        if (modalRequestRef.current !== requestId) return;
        setModalEntries(payload.data || []);
      })
      .catch((error) => {
        if (modalRequestRef.current !== requestId) return;
        setModalEntries([]);
        toast.error(toErrorMessage(error));
      })
      .finally(() => {
        if (modalRequestRef.current !== requestId) return;
        setModalLoading(false);
      });
  }

  function closeDayModal() {
    modalRequestRef.current += 1;
    setModalDate(null);
    setModalEntries([]);
    setModalLoading(false);
  }

  useEffect(() => {
    if (!exercise) return;
    if (pendingRestRestoreRef.current) return;
    const openIndex = firstOpenSetIndex(exercise.planned_sets);
    setSetIndex(openIndex);
    const openSet = exercise.planned_sets[openIndex];
    syncSteppersFromSet(openSet);
    setCueTab('key_points');
    setEditingSet(false);
    enterSetFromLoaded(openSet);
  }, [exerciseIndex, exercise?.id]);

  useEffect(() => {
    const pending = pendingRestRestoreRef.current;
    if (!pending || !session) return;
    pendingRestRestoreRef.current = null;
    if (pending.context) setRestModalContext(pending.context);
    applyRestFromAttempt(pending.set);
  }, [session, exerciseIndex, setIndex]);

  useInterval(() => {
    if (!isActive) return;
    setElapsed((value) => value + 1);
    setRemaining((value) => Math.max(0, value - 1));
  }, isActive ? 1000 : null);

  useInterval(() => {
    const sessionId = session?.workout_session_id;
    if (!isActive || !sessionId) return;
    heartbeatWorkoutSession(sessionId).catch(() => {});
  }, isActive && session?.workout_session_id ? 10000 : null);

  useInterval(() => {
    if (!session?.workout_session_id) return;
    if (!hrBufferRef.current.length) return;
    flushHeartRateSamples();
  }, session?.workout_session_id ? 10000 : null);

  useEffect(() => {
    hrSessionIdRef.current = session?.workout_session_id || null;
  }, [session?.workout_session_id]);

  useEffect(() => {
    function onPageHide() {
      void flushHeartRateSamples();
    }
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      void flushHeartRateSamples();
      // Do not disconnect BLE on React remount/HMR — that drops the strap mid-session.
    };
  }, []);

  useInterval(() => {
    setRestSeconds((value) => {
      if (value <= 0) return 0;
      return value - 1;
    });
  }, restSeconds > 0 && !showSetTimerRail ? 1000 : null);

  useEffect(() => {
    if (!restWasActiveRef.current) return;
    if (restSeconds > 0) return;
    if (restModalMode === 'hidden') return;
    if (restEndingFlash) return;
    beginRestEndFlash();
  }, [restSeconds, restModalMode, restEndingFlash]);

  useInterval(() => {
    if (!setStartedAt) {
      setSetClockSeconds(0);
      return;
    }
    const startedMs = Date.parse(setStartedAt);
    if (!Number.isFinite(startedMs)) {
      setSetClockSeconds(0);
      return;
    }
    setSetClockSeconds(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
  }, setStartedAt && !setFinished ? 1000 : null);

  const railSeconds = showSetTimerRail
    ? setClockSeconds
    : showRestTimerRail
      ? restSeconds
      : 0;
  const restProgress = useMemo(() => {
    if (showSetTimerRail) {
      // count-up ring: fill grows with elapsed; soft cap at 3 minutes for visual
      const cap = Math.max(180, setClockSeconds);
      return Math.min(1, setClockSeconds / cap);
    }
    if (!restTotal) return 0;
    return Math.min(1, Math.max(0, restSeconds / restTotal));
  }, [showSetTimerRail, setClockSeconds, restSeconds, restTotal]);

  const ringCircumference = 2 * Math.PI * 60;
  const ringOffset = ringCircumference * (1 - restProgress);

  function enterSetFromLoaded(set) {
    if (set?.outcome === 1 && set?.started_at && set?.set_attempt_id) {
      setSetStartedAt(set.started_at);
      setActiveAttemptId(set.set_attempt_id);
      const startedMs = Date.parse(set.started_at);
      setSetClockSeconds(
        Number.isFinite(startedMs)
          ? Math.max(0, Math.floor((Date.now() - startedMs) / 1000))
          : 0,
      );
      return;
    }
    setSetStartedAt(null);
    setSetClockSeconds(0);
    setActiveAttemptId(set?.set_attempt_id || null);
  }

  function focusExercise(index) {
    setExerciseIndex(index);
    setEditingSet(false);
    setCompleteModalOpen(false);
  }

  function startRest(seconds) {
    const total = Math.max(0, Number(seconds) || 0);
    if (total <= 0) {
      clearRestUi();
      return;
    }
    setRestTotal(total);
    setRestSeconds(total);
    restWasActiveRef.current = true;
    setRestModalMode('expanded');
  }

  function beginRestEndFlash() {
    if (restGoActiveRef.current) return;
    restGoActiveRef.current = true;
    setRestEndingFlash(true);
    setRestSeconds(0);
    // Always expand so GO is dismissible even if rest was minimized.
    setRestModalMode('expanded');
  }

  function dismissRestGo() {
    if (!restGoActiveRef.current && !restEndingFlash) return;
    clearRestUi();
  }

  function syncSteppersFromSet(set) {
    setWeight(toWholeLb(set?.actual_weight ?? set?.prescribed_weight) ?? 0);
    setReps(Number(set?.actual_reps ?? set?.prescribed_reps) || 0);
    setRpe(Number(set?.rpe) || 8);
    setActiveAttemptId(set?.set_attempt_id || null);
  }

  function advanceAfterSet(nextSession, targetExercise, rest) {
    setRemaining(remainingRestSeconds(nextSession));
    setEditingSet(false);
    setCompleteModalOpen(false);
    setSetStartedAt(null);
    setSetClockSeconds(0);

    if (targetExercise.planned_sets.every((set) => set.completed)) {
      const nextIdx = nextSession.exercises.findIndex(
        (item, index) =>
          index > exerciseIndex &&
          item.planned_sets.some((set) => !set.completed),
      );
      if (rest > 0) startRest(rest);
      if (nextIdx >= 0) {
        setExerciseIndex(nextIdx);
      }
      return;
    }

    const nextOpen = firstOpenSetIndex(targetExercise.planned_sets);
    if (rest > 0) startRest(rest);
    setSetIndex(nextOpen);
    syncSteppersFromSet(targetExercise.planned_sets[nextOpen]);
    enterSetFromLoaded(targetExercise.planned_sets[nextOpen]);
  }

  function applySessionStats(stats) {
    if (!stats) return;
    if (stats.volume_lb != null) {
      setVolume(Number(stats.volume_lb) || 0);
    }
    if (stats.elapsed_seconds != null) {
      setElapsed(Number(stats.elapsed_seconds) || 0);
    }
  }

  async function onStartSet() {
    if (!isActive || !exercise || !activeSet || !session || busy || setFinished)
      return;
    if (setStartedAt || completeModalOpen || restSeconds > 0) return;

    setBusy(true);
    try {
      const started = await startSetAttempt({
        workoutSlotId: session.workout_slot_id,
        workoutSessionId: session.workout_session_id,
        plannedSetId: activeSet.id,
      });

      const startedAt = started.started_at || new Date().toISOString();
      const nextSession = JSON.parse(JSON.stringify(session));
      const targetSet =
        nextSession.exercises[exerciseIndex].planned_sets[setIndex];
      targetSet.set_attempt_id = started.id;
      targetSet.outcome = 1;
      targetSet.started_at = startedAt;
      setSession(nextSession);
      setActiveAttemptId(started.id);
      setSetStartedAt(startedAt);
      setSetClockSeconds(0);
      setRestSeconds(0);
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function requestCompleteSet() {
    if (!isActive || !activeSet || !setStartedAt || completeModalOpen || busy)
      return;
    const attemptId = activeSet.set_attempt_id || activeAttemptId;
    if (!attemptId) return;

    syncSteppersFromSet({
      ...activeSet,
      actual_weight: activeSet.prescribed_weight,
      actual_reps: activeSet.prescribed_reps,
      rpe: activeSet.rpe || 8,
    });
    setCompleteModalOpen(true);
  }

  function cancelCompleteModal() {
    setCompleteModalOpen(false);
  }

  async function onSubmitSetFromModal() {
    if (!isActive || !exercise || !activeSet || !session || busy) return;
    if (!completeModalOpen) return;

    setBusy(true);
    try {
      await flushHeartRateSamples();
      const completed = await completeSetAttempt({
        workoutSlotId: session.workout_slot_id,
        workoutSessionId: session.workout_session_id,
        plannedSetId: activeSet.id,
        setAttemptId: activeSet.set_attempt_id || activeAttemptId || null,
        actualWeight: weight || null,
        actualReps: reps || null,
        actualRpe: rpe || null,
      });

      const prescribedRest = Number(activeSet.prescribed_rest_seconds) || 0;
      const completedExerciseName = exercise.name;
      const completedSetIndex = setIndex;
      const completedSetCount = (exercise.planned_sets || []).length;

      const nextSession = JSON.parse(JSON.stringify(session));
      const targetExercise = nextSession.exercises[exerciseIndex];
      const targetSet = targetExercise.planned_sets[setIndex];
      targetSet.completed = true;
      targetSet.outcome = 2;
      targetSet.actual_weight =
        toWholeLb(completed.actual_weight ?? weight) ?? null;
      targetSet.actual_reps = completed.actual_reps ?? reps ?? null;
      targetSet.rpe = completed.actual_rpe ?? rpe;
      targetSet.set_attempt_id = completed.id;
      targetSet.ended_at = completed.ended_at || null;
      targetSet.rest_started_at = null;
      targetSet.rest_ends_at = null;

      applySessionStats(completed.session_stats);
      setCompleteModalOpen(false);
      setSetStartedAt(null);
      setSetClockSeconds(0);
      setActiveAttemptId(completed.id || null);

      if (isSessionCompleteResponse(completed)) {
        setSession(nextSession);
        enterSessionCompleted(completed.session_stats, completed.new_awards || []);
        return;
      }

      if (Array.isArray(completed.new_awards) && completed.new_awards.length) {
        const names = completed.new_awards
          .map((award) => award.achievement?.name || award.name)
          .filter(Boolean);
        if (names.length) {
          toast.success(`Trophy Case: ${names.join(', ')}`);
        }
      }

      setSession(nextSession);

      if (!sessionHasOpenSets(nextSession) && nextSession.source === 'custom') {
        clearRestUi();
        setEditingSet(false);
        setCustomContinueOpen(true);
        return;
      }

      let restPayload = null;
      if (prescribedRest > 0) {
        restPayload = await startRestAttempt(completed.id, prescribedRest);
        targetSet.rest_started_at = restPayload.rest_started_at || null;
        targetSet.rest_ends_at = restPayload.rest_ends_at || null;
        setSession(JSON.parse(JSON.stringify(nextSession)));
      }

      const focusIdx = applyFocusFromResponse(
        nextSession,
        completed.current_workout_exercise_id,
      );
      let nextExIdx = exerciseIndex;
      let nextSi = setIndex;
      if (focusIdx != null) {
        nextExIdx = focusIdx;
        nextSi = firstOpenSetIndex(
          nextSession.exercises[focusIdx]?.planned_sets || [],
        );
      } else {
        const nextOpen = findNextOpenAfter(
          nextSession,
          exerciseIndex,
          setIndex,
        );
        if (nextOpen) {
          nextExIdx = nextOpen.exerciseIndex;
          nextSi = nextOpen.setIndex;
        }
      }

      const nextExercise = nextSession.exercises[nextExIdx];
      const nextSet = nextExercise?.planned_sets?.[nextSi];

      setRemaining(remainingRestSeconds(nextSession));
      setEditingSet(false);
      setExerciseIndex(nextExIdx);
      setSetIndex(nextSi);
      if (nextSet) {
        syncSteppersFromSet(nextSet);
        enterSetFromLoaded(nextSet);
      }

      if (restPayload) {
        const remainingRest =
          restPayload.rest_remaining_seconds ??
          restRemainingFromEndsAt(restPayload.rest_ends_at);
        const total =
          restTotalFromWindow(
            restPayload.rest_started_at,
            restPayload.rest_ends_at,
          ) || remainingRest;
        if (remainingRest > 0) {
          setRestModalContext(
            buildRestModalContext({
              exerciseName: completedExerciseName,
              setIndex: completedSetIndex,
              setCount: completedSetCount,
              completedSet: targetSet,
              nextExercise,
              nextSet,
            }),
          );
          setRestTotal(total || remainingRest);
          setRestSeconds(remainingRest);
          setRestAttemptId(restPayload.id || completed.id);
          restWasActiveRef.current = true;
          setRestEndingFlash(false);
          setRestModalMode('expanded');
        } else {
          clearRestUi();
        }
      } else {
        clearRestUi();
      }
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function onCompleteSet() {
    if (!isActive || !exercise || !activeSet || !session || busy) return;

    const isEditSave = Boolean(activeSet.completed);
    if (!isEditSave || !editingSet) return;

    setBusy(true);
    try {
      const completed = await completeSetAttempt({
        workoutSlotId: session.workout_slot_id,
        workoutSessionId: session.workout_session_id,
        plannedSetId: activeSet.id,
        setAttemptId: activeSet.set_attempt_id || activeAttemptId || null,
        actualWeight: weight || null,
        actualReps: reps || null,
        actualRpe: rpe || null,
      });

      const nextSession = JSON.parse(JSON.stringify(session));
      const targetExercise = nextSession.exercises[exerciseIndex];
      const targetSet = targetExercise.planned_sets[setIndex];
      targetSet.completed = true;
      targetSet.outcome = 2;
      targetSet.actual_weight =
        toWholeLb(completed.actual_weight ?? weight) ?? null;
      targetSet.actual_reps = completed.actual_reps ?? reps ?? null;
      targetSet.rpe = completed.actual_rpe ?? rpe;
      targetSet.set_attempt_id = completed.id;

      applySessionStats(completed.session_stats);
      setSession(nextSession);
      setActiveAttemptId(completed.id || null);
      setEditingSet(false);
      syncSteppersFromSet(targetSet);
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function requestSkipSet() {
    if (!canSkipSet || busy) return;
    setSkipSetConfirmOpen(true);
  }

  async function onSkipSet() {
    if (!isActive || !exercise || !activeSet || !session || busy) return;
    if (activeSet.completed) {
      onSkipSetLocalOnly();
      return;
    }

    setSkipSetConfirmOpen(false);
    setBusy(true);
    try {
      const skipped = await skipSetAttempt({
        workoutSlotId: session.workout_slot_id,
        workoutSessionId: session.workout_session_id,
        plannedSetId: activeSet.id,
      });

      const nextSession = JSON.parse(JSON.stringify(session));
      const targetExercise = nextSession.exercises[exerciseIndex];
      const targetSet = targetExercise.planned_sets[setIndex];
      targetSet.completed = true;
      targetSet.outcome = 3;
      targetSet.set_attempt_id = skipped.id;
      setSession(nextSession);
      setActiveAttemptId(null);
      setEditingSet(false);
      setSetStartedAt(null);
      setSetClockSeconds(0);
      setCompleteModalOpen(false);

      applySessionStats(skipped.session_stats);

      if (isSessionCompleteResponse(skipped)) {
        enterSessionCompleted(skipped.session_stats, skipped.new_awards || []);
        return;
      }

      if (!sessionHasOpenSets(nextSession) && nextSession.source === 'custom') {
        clearRestUi();
        setCustomContinueOpen(true);
        return;
      }

      if (Array.isArray(skipped.new_awards) && skipped.new_awards.length) {
        const names = skipped.new_awards
          .map((award) => award.achievement?.name || award.name)
          .filter(Boolean);
        if (names.length) {
          toast.success(`Trophy Case: ${names.join(', ')}`);
        }
      }

      const focusIdx = applyFocusFromResponse(
        nextSession,
        skipped.current_workout_exercise_id,
      );
      if (focusIdx != null && focusIdx !== exerciseIndex) {
        setRemaining(remainingRestSeconds(nextSession));
        setExerciseIndex(focusIdx);
      } else {
        advanceAfterSet(nextSession, targetExercise, 0);
      }
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function onSkipSetLocalOnly() {
    const nextOpen = sets.findIndex(
      (set, index) => index > setIndex && !set.completed,
    );
    if (nextOpen >= 0) {
      setSetIndex(nextOpen);
      syncSteppersFromSet(sets[nextOpen]);
      return;
    }

    const nextIdx = session.exercises.findIndex(
      (item, index) =>
        index > exerciseIndex &&
        item.planned_sets.some((set) => !set.completed),
    );
    if (nextIdx >= 0) {
      focusExercise(nextIdx);
    }
  }

  function requestUndoSkip() {
    if (!isActive || !exercise || !activeSet || !session || busy) return;
    if (activeSet.outcome !== 3) return;
    setUnskipSetConfirmOpen(true);
  }

  async function onUnskipSet() {
    if (!isActive || !exercise || !activeSet || !session || busy) return;
    if (activeSet.outcome !== 3) return;

    setUnskipSetConfirmOpen(false);
    setBusy(true);
    try {
      const reopened = await unskipSetAttempt({
        workoutSlotId: session.workout_slot_id,
        workoutSessionId: session.workout_session_id,
        plannedSetId: activeSet.id,
        setAttemptId: activeSet.set_attempt_id || activeAttemptId || null,
      });

      const nextSession = JSON.parse(JSON.stringify(session));
      const targetExercise = nextSession.exercises[exerciseIndex];
      const targetSet = targetExercise.planned_sets[setIndex];
      targetSet.completed = false;
      targetSet.outcome = 1;
      targetSet.actual_weight = null;
      targetSet.actual_reps = null;
      targetSet.rpe = null;
      targetSet.set_attempt_id = reopened.id;
      targetSet.started_at = reopened.started_at || null;
      setSession(nextSession);
      setEditingSet(false);
      setSetStartedAt(null);
      setSetClockSeconds(0);
      setCompleteModalOpen(false);
      setActiveAttemptId(reopened.id || null);
      syncSteppersFromSet({
        ...targetSet,
        rpe: targetSet.estimated_rpe || 8,
        set_attempt_id: reopened.id,
      });
      applySessionStats(reopened.session_stats);

      const focusIdx = applyFocusFromResponse(
        nextSession,
        reopened.current_workout_exercise_id,
      );
      if (focusIdx != null && focusIdx !== exerciseIndex) {
        setExerciseIndex(focusIdx);
        const focusSets = nextSession.exercises[focusIdx]?.planned_sets || [];
        const openIdx = firstOpenSetIndex(focusSets);
        setSetIndex(openIdx);
        syncSteppersFromSet(focusSets[openIdx]);
      }
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function onSkipWorkout() {
    if (!canSkipWorkout || skippingWorkout || busy) return;
    if (
      !window.confirm(
        'Skip today’s workout? The next lift will move into today.',
      )
    ) {
      return;
    }

    setSkippingWorkout(true);
    try {
      await flushHeartRateSamples();
      const payload = await skipWorkoutSlot(session.workout_slot_id);
      if (payload?.rest || !payload?.id) {
        toast.success('Workout skipped — rest day now');
        navigate('/');
        return;
      }
      toast.success('Workout skipped — pick the next workout');
      const [nextHome, startablePayload] = await Promise.all([
        fetchAthleteHome(),
        fetchStartableWorkouts(),
      ]);
      setHome(nextHome);
      setProfile(nextHome?.profile || null);
      const items = startablePayload.data || [];
      setStartable(items);
      if (items.length === 1) {
        await loadStartableItem(items[0], nextHome);
      } else if (items.length > 1) {
        clearSessionState();
        setPhase('picker');
      } else {
        clearSessionState();
        setPhase('rest');
      }
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setSkippingWorkout(false);
    }
  }

  async function onStartSession() {
    if (!session || starting || busy) return;
    if (session.source === 'custom') {
      setPhase('active');
      return;
    }
    setStarting(true);
    try {
      let started;
      if (session.source === 'overlay') {
        started = await startOverlaySession(
          session.athlete_calendar_event_id,
          session.on_date,
        );
      } else {
        started = await startSlotSession(session.workout_slot_id);
      }
      setSession((current) => ({
        ...current,
        workout_session_id: started?.id || null,
      }));
      setPhase('active');
      if (started?.session_stats?.elapsed_seconds != null) {
        setElapsed(Number(started.session_stats.elapsed_seconds) || 0);
      } else if (started?.started_at) {
        const startedMs = Date.parse(started.started_at);
        if (Number.isFinite(startedMs)) {
          setElapsed(Math.max(0, Math.floor((Date.now() - startedMs) / 1000)));
        } else {
          setElapsed(0);
        }
      } else {
        setElapsed(0);
      }
      if (started?.session_stats?.volume_lb != null) {
        setVolume(Number(started.session_stats.volume_lb) || 0);
      } else {
        setVolume(0);
      }
      toast.success('Workout started');
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setStarting(false);
    }
  }

  async function onBeginCustomFromIdle() {
    if (busy || starting) return;
    setBusy(true);
    try {
      await bootstrapCustomSession(home);
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function onConfirmAddExercise({ exerciseId, sets, reps, restSeconds }) {
    if (!session?.workout_session_id || addingExercise) return;
    setAddingExercise(true);
    try {
      await addExerciseToWorkoutSession(session.workout_session_id, {
        exerciseId,
        sets,
        reps,
        restSeconds,
      });
      const payload = await loadWorkout({
        source: 'custom',
        workoutSessionId: session.workout_session_id,
      });
      applyLoadedPayload(home, payload);
      setAddExerciseOpen(false);
      setCustomContinueOpen(false);
      toast.success('Exercise added');
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setAddingExercise(false);
    }
  }

  async function onEndCustomWorkout() {
    if (!session?.workout_session_id || endingCustom) return;
    setEndingCustom(true);
    try {
      await flushHeartRateSamples();
      const completed = await completeWorkoutSession(session.workout_session_id);
      setEndCustomConfirmOpen(false);
      setCustomContinueOpen(false);
      enterSessionCompleted(completed?.session_stats, completed?.new_awards || []);
      toast.success('Custom workout saved');
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setEndingCustom(false);
    }
  }

  async function onPickStartable(item) {
    setBusy(true);
    try {
      await loadStartableItem(item);
    } catch (error) {
      toast.error(toErrorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  function onEditThisSet() {
    if (!activeSet?.completed || activeSet.outcome === 3) return;
    setEditingSet(true);
    setAdjustmentsOpen(true);
    syncSteppersFromSet(activeSet);
  }

  function onCancelEditSet() {
    setEditingSet(false);
    syncSteppersFromSet(activeSet);
  }

  async function onExtraRest(seconds = 10) {
    if (showSetTimerRail || busy || !restUiActive) return;
    const delta = Number(seconds) || 0;
    if (!delta) return;
    if (delta < 0 && restSeconds + delta <= 0) {
      await onSkipRest();
      return;
    }
    const attemptId = restAttemptId || activeAttemptId;
    if (!attemptId) {
      setRestSeconds((value) => Math.max(0, value + delta));
      setRestTotal((value) => Math.max(0, value + delta));
      return;
    }
    try {
      const updated = await extraRestSetAttempt(attemptId, delta);
      const remainingRest =
        updated.rest_remaining_seconds ??
        restRemainingFromEndsAt(updated.rest_ends_at);
      const total =
        restTotalFromWindow(updated.rest_started_at, updated.rest_ends_at) ||
        remainingRest;
      if (remainingRest <= 0) {
        beginRestEndFlash();
        return;
      }
      setRestSeconds(remainingRest);
      setRestTotal(total || remainingRest);
      setRestAttemptId(updated.id || attemptId);
    } catch (error) {
      toast.error(toErrorMessage(error));
    }
  }

  async function onSkipRest() {
    if (showSetTimerRail || busy || !restUiActive) return;
    const attemptId = restAttemptId || activeAttemptId;
    try {
      if (attemptId) {
        await endRestAttempt(attemptId);
        if (session) {
          const nextSession = JSON.parse(JSON.stringify(session));
          nextSession.exercises.forEach((item) => {
            (item.planned_sets || []).forEach((set) => {
              if (set.set_attempt_id === attemptId) {
                set.rest_ends_at = new Date().toISOString();
              }
            });
          });
          setSession(nextSession);
        }
      }
      beginRestEndFlash();
    } catch (error) {
      toast.error(toErrorMessage(error));
    }
  }

  const musicHeader = musicDockHidden ? (
    <div className={classes.topMusic}>
      <button
        type="button"
        className={classes.topMusicRestore}
        onClick={() => setMusicDockHidden(false)}
        aria-label="Show music player"
      >
        <IconMusic size={16} stroke={2} />
        Music
      </button>
    </div>
  ) : (
    <div className={classes.topMusic}>
      <AppleMusicMiniPlayer
        mode="header"
        onHide={() => setMusicDockHidden(true)}
      />
    </div>
  );

  if (loading) {
    return (
      <div className={classes.page}>
        <header className={classes.topBar}>
          <div className={classes.topIdentity}>
            <p className={classes.sessionEyebrow}>Loading workout…</p>
          </div>
          {musicHeader}
          <div className={classes.topMeta} />
        </header>
      </div>
    );
  }

  const restMessage =
    loadError ||
    'Nothing to start today — rest day, or your next lift is upcoming.';

  return (
    <div className={classes.page}>
      <header className={classes.topBar}>
        <div className={classes.topIdentity}>
          <p className={classes.sessionEyebrow}>Workout</p>
          <h1 className={classes.athleteName}>{displayName}</h1>
          {profile ? (
            <p className={classes.profileLine}>
              {profile.sport} | {profile.position} | {profile.bodyweight}{' '}
              {(profile.unit || '').toUpperCase()}
            </p>
          ) : null}
        </div>
        {musicHeader}
        <div className={classes.topMeta}>
          <span className={classes.dateTime}>{formatNow()}</span>
          {hrStatus === 'unsupported' ? (
            <span className={classes.hrUnsupported} title="Web Bluetooth unavailable">
              HR N/A
            </span>
          ) : hrStatus === 'connected' ? (
            <button
              type="button"
              className={classes.exitBtn}
              onClick={onDisconnectHeartRate}
            >
              Disconnect HR
            </button>
          ) : (
            <button
              type="button"
              className={classes.exitBtn}
              onClick={onConnectHeartRate}
              disabled={hrStatus === 'connecting'}
            >
              {hrStatus === 'connecting' ? 'Connecting…' : 'Connect HR'}
            </button>
          )}
          {canSkipWorkout ? (
            <button
              type="button"
              className={classes.exitBtn}
              onClick={onSkipWorkout}
              disabled={skippingWorkout || busy}
            >
              <IconPlayerSkipForward size={16} stroke={2} />
              {skippingWorkout ? 'Skipping…' : 'Skip Workout'}
            </button>
          ) : null}
          <button type="button" className={classes.exitBtn} onClick={leaveWorkout}>
            <IconLogout size={16} stroke={2} />
            Leave
          </button>
        </div>
      </header>

      <div className={classes.progressStrip}>
        <p className={classes.breadcrumb}>
          {crumbs.map((part, index) => (
            <span key={`${part}-${index}`}>
              {index > 0 ? (
                <span className={classes.breadcrumbSep}> &gt; </span>
              ) : null}
              {part}
            </span>
          ))}
        </p>
        <div className={classes.progressMeta}>
          <span>
            {isCompleted
              ? 'Complete'
              : hasPrimedSession
                ? totalSetCount > 0
                  ? `${finishedSetCount} of ${totalSetCount} sets`
                  : `${progressCount} of ${session.exercises.length}`
                : phase === 'picker'
                  ? 'Pick one'
                  : isCustomEmpty
                    ? 'Custom'
                    : 'Rest day'}
          </span>
        </div>
        <div className={classes.progressTrack}>
          <div
            className={classes.progressFill}
            style={{ width: `${exerciseProgress * 100}%` }}
          />
        </div>
      </div>

      <div className={classes.layout}>
        <div className={classes.center}>
          {phase === 'picker' ? (
            <section className={classes.card}>
              <p className={classes.exerciseEyebrow}>Today&apos;s Options</p>
              <h2 className={classes.exerciseName}>Load a workout</h2>
              <p className={classes.demoText}>
                Pick which session to load, then start it when you&apos;re ready.
              </p>
              <ul className={classes.queueList}>
                {startable.map((item) => (
                  <li
                    key={`${item.source}-${item.workout_slot_id || item.athlete_calendar_event_id}`}
                  >
                    <button
                      type="button"
                      className={`${classes.queueItem} ${classes.queueItemActive}`}
                      onClick={() => onPickStartable(item)}
                      disabled={busy}
                    >
                      <span className={classes.queueMark}>
                        {item.source === 'overlay' ? 'O' : 'T'}
                      </span>
                      <span className={classes.queueName}>
                        {item.program_name}
                        {item.session_label ? ` · ${item.session_label}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={classes.secondaryBtn}
                onClick={onBeginCustomFromIdle}
                disabled={busy}
                style={{ marginTop: '1rem', width: '100%' }}
              >
                <IconPlus size={16} stroke={2.5} />
                Custom Workout
              </button>
            </section>
          ) : isCustomEmpty ? (
            <section className={classes.card}>
              <p className={classes.exerciseEyebrow}>Custom session</p>
              <h2 className={classes.exerciseName}>Custom Workout</h2>
              <p className={classes.demoText}>
                Add an exercise to get started. You can keep adding as you go.
              </p>
              <button
                type="button"
                className={classes.completeBtn}
                onClick={() => setAddExerciseOpen(true)}
                disabled={busy}
              >
                <IconPlus size={18} stroke={2.5} />
                Add exercise
              </button>
              <button
                type="button"
                className={classes.secondaryBtn}
                onClick={() => setEndCustomConfirmOpen(true)}
                disabled={busy || endingCustom}
                style={{ marginTop: '0.75rem', width: '100%' }}
              >
                End Workout
              </button>
            </section>
          ) : isCompleted ? (
            <section className={classes.card}>
              <p className={classes.exerciseEyebrow}>Session complete</p>
              <h2 className={classes.exerciseName}>Workout complete</h2>
              <p className={classes.demoText}>
                Nice work. Your session is saved and closed.
              </p>
              <div className={classes.prescriptionRow}>
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Volume</p>
                  <p className={classes.prescriptionValue}>
                    {volume.toLocaleString()}
                    <span className={classes.prescriptionUnit}> lb</span>
                  </p>
                </div>
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Elapsed</p>
                  <p className={classes.prescriptionValue}>
                    {formatClock(elapsed)}
                  </p>
                </div>
              </div>
              <NewAwardsList awards={newAwards} />
              <button
                type="button"
                className={classes.completeBtn}
                onClick={leaveWorkout}
              >
                Back to Home
              </button>
            </section>
          ) : hasPrimedSession && exercise ? (
          <section className={classes.card}>
            <p className={classes.exerciseEyebrow}>
              {isLoaded ? 'Ready to start:' : 'Current Exercise:'}
            </p>
            <h2 className={classes.exerciseName}>{exercise.name}</h2>
            <div className={classes.tags}>
              {exercise.category ? (
                <span className={classes.tag}>{exercise.category}</span>
              ) : null}
              {exercise.movement_pattern ? (
                <span className={classes.tag}>{exercise.movement_pattern}</span>
              ) : null}
              {exercise.body_region ? (
                <span className={classes.tag}>{exercise.body_region}</span>
              ) : null}
            </div>

            <ExerciseDemo
              key={exercise.id || exercise.name}
              assets={exercise.assets}
              exerciseName={exercise.name}
              tagsByVariety={exercise.tags_by_variety}
            />

            <div className={classes.tabs}>
              <button
                type="button"
                className={`${classes.tab}${
                  cueTab === 'key_points' ? ` ${classes.tabActive}` : ''
                }`}
                onClick={() => setCueTab('key_points')}
              >
                Key Points
              </button>
              <button
                type="button"
                className={`${classes.tab}${
                  cueTab === 'muscles' ? ` ${classes.tabActive}` : ''
                }`}
                onClick={() => setCueTab('muscles')}
              >
                Muscles
              </button>
            </div>

            {cueTab === 'key_points' ? (
              <ul className={classes.cueList}>
                {(exercise.coaching_cues || []).map((cue) => (
                  <li key={cue} className={classes.cueItem}>
                    <IconCheck
                      size={18}
                      className={classes.cueIcon}
                      stroke={2.5}
                    />
                    <span>{cue}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <ul className={classes.cueList}>
                <li className={classes.cueItem}>
                  <IconCheck
                    size={18}
                    className={classes.cueIcon}
                    stroke={2.5}
                  />
                  <span>Primary: {exercise.primary_muscle || '—'}</span>
                </li>
                <li className={classes.cueItem}>
                  <IconCheck
                    size={18}
                    className={classes.cueIcon}
                    stroke={2.5}
                  />
                  <span>
                    Secondary:{' '}
                    {(exercise.secondary_muscles || []).join(', ') || '—'}
                  </span>
                </li>
              </ul>
            )}

            <div className={classes.prescriptionRow}>
              {prescription.sets != null ? (
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Target Sets</p>
                  <p className={classes.prescriptionValue}>
                    {prescription.sets}
                  </p>
                </div>
              ) : null}
              {prescription.reps != null ? (
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Target Reps</p>
                  <p className={classes.prescriptionValue}>
                    {prescription.reps}
                  </p>
                </div>
              ) : null}
              {prescription.weight != null ? (
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Target Weight</p>
                  <p className={classes.prescriptionValue}>
                    {prescription.weight}
                    <span className={classes.prescriptionUnit}> lb</span>
                  </p>
                </div>
              ) : null}
              {prescription.style ? (
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Style</p>
                  <p className={classes.prescriptionValue}>
                    {prescription.style}
                  </p>
                </div>
              ) : null}
              {prescription.rest ? (
                <div className={classes.prescriptionCard}>
                  <p className={classes.prescriptionLabel}>Rest</p>
                  <p className={classes.prescriptionValue}>
                    {prescription.rest}
                  </p>
                </div>
              ) : null}
            </div>
            {exercise.history ? (
              <p className={classes.historyLine}>
                <span>Last time ({exercise.history.last_time})</span>
                <span className={classes.historyDot}>·</span>
                <span>Best ({exercise.history.best})</span>
              </p>
            ) : null}

            {showOnCardAdjustments ? (
              <>
            <button
              type="button"
              className={classes.adjustmentsToggle}
              onClick={() => setAdjustmentsOpen((value) => !value)}
              aria-expanded={adjustmentsOpen}
            >
              <span>My Adjustments</span>
              <IconChevronDown
                size={18}
                stroke={2.5}
                className={
                  adjustmentsOpen
                    ? classes.adjustmentsChevronOpen
                    : classes.adjustmentsChevron
                }
              />
            </button>

            {adjustmentsOpen ? (
            <div className={classes.steppers}>
              <div className={classes.stepper}>
                <p className={classes.stepperLabel}>Weight (lb)</p>
                <div className={classes.stepperControls}>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={1}
                    className={classes.stepValueInput}
                    value={weight > 0 ? weight : ''}
                    placeholder="—"
                    disabled={steppersLocked || busy}
                    aria-label="Weight in pounds"
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => {
                      const raw = event.target.value;
                      if (raw === '') {
                        setWeight(0);
                        return;
                      }
                      const next = Number(raw);
                      if (!Number.isFinite(next) || next < 0) return;
                      setWeight(toWholeLb(next) ?? 0);
                    }}
                  />
                </div>
              </div>
              <div className={classes.stepper}>
                <p className={classes.stepperLabel}>Reps</p>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={steppersLocked || busy}
                    onClick={() => setReps((value) => Math.max(0, value - 1))}
                  >
                    −
                  </button>
                  <span className={classes.stepValue}>{reps || '—'}</span>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={steppersLocked || busy}
                    onClick={() => setReps((value) => value + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className={classes.stepper}>
                <p className={classes.stepperLabel}>RPE</p>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={steppersLocked || busy}
                    onClick={() => setRpe((value) => Math.max(1, value - 1))}
                  >
                    −
                  </button>
                  <span className={classes.stepValue}>{rpe}</span>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={steppersLocked || busy}
                    onClick={() => setRpe((value) => Math.min(10, value + 1))}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            ) : null}
              </>
            ) : null}

            <div className={classes.setTrack}>
              <div className={classes.setTrackHeader}>
                <p className={classes.setLabel}>
                  Set {Math.min(setIndex + 1, sets.length)} of {sets.length}
                </p>
                {isActive && setFinished && !editingSet ? (
                  <p
                    className={
                      setSkipped
                        ? classes.skippedBadge
                        : classes.completedBadge
                    }
                  >
                    <IconCheck size={16} stroke={3} />
                    <span>{setSkipped ? 'Skipped' : 'Completed'}</span>
                  </p>
                ) : null}
              </div>
              <div className={classes.setRings}>
                {sets.map((set, index) => (
                  <button
                    key={set.id || set.position}
                    type="button"
                    className={[
                      classes.setRing,
                      set.completed ? classes.setRingDone : '',
                      index === setIndex ? classes.setRingActive : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setSetIndex(index);
                      setEditingSet(false);
                      setCompleteModalOpen(false);
                      syncSteppersFromSet(set);
                      enterSetFromLoaded(set);
                    }}
                  >
                    {set.outcome === 3 ? (
                      <IconX
                        size={18}
                        stroke={3}
                        className={classes.setRingSkipIcon}
                      />
                    ) : set.completed ? (
                      <IconCheck size={18} stroke={3} />
                    ) : (
                      index + 1
                    )}
                  </button>
                ))}
              </div>
            </div>

            {isLoaded ? (
              <button
                type="button"
                className={classes.completeBtn}
                onClick={onStartSession}
                disabled={busy || starting}
              >
                <IconPlayerPlay size={20} stroke={3} />
                {starting ? 'Starting…' : 'Start Workout'}
              </button>
            ) : editingSet ? (
              <button
                type="button"
                className={classes.completeBtn}
                onClick={onCompleteSet}
                disabled={busy}
              >
                <IconCheck size={20} stroke={3} />
                {busy ? 'Saving…' : 'Save'}
              </button>
            ) : setSkipped ? (
              <button
                type="button"
                className={classes.completeBtn}
                onClick={requestUndoSkip}
                disabled={busy}
              >
                Undo Skip
              </button>
            ) : canEditCompletedSet ? (
              <button
                type="button"
                className={classes.completeBtn}
                onClick={onEditThisSet}
                disabled={busy}
              >
                Edit This Set
              </button>
            ) : setFinished ? null : setInProgress || completeModalOpen ? (
              <button
                type="button"
                className={classes.setLiveBtn}
                onClick={requestCompleteSet}
                disabled={busy || completeModalOpen}
              >
                <span className={classes.setLiveClock}>
                  {formatClock(setClockSeconds)}
                </span>
                <span className={classes.setLiveHint}>Tap to complete</span>
              </button>
            ) : (
              <button
                type="button"
                className={
                  isResting ? classes.startSetBtnResting : classes.startSetBtn
                }
                onClick={onStartSet}
                disabled={busy || starting || isResting}
              >
                {isResting ? (
                  'Resting'
                ) : (
                  <>
                    <IconPlayerPlay size={20} stroke={3} />
                    Start Set
                  </>
                )}
              </button>
            )}
            {isActive ? (
              editingSet ? (
                <div className={classes.secondaryRow}>
                  <button
                    type="button"
                    className={classes.secondaryBtn}
                    onClick={onCancelEditSet}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  {canSkipSet ? (
                    <button
                      type="button"
                      className={classes.skipSetLink}
                      onClick={requestSkipSet}
                      disabled={busy}
                    >
                      Skip set
                    </button>
                  ) : null}
                  {isCustomSession ? (
                    <div className={classes.secondaryRow}>
                      <button
                        type="button"
                        className={classes.secondaryBtn}
                        onClick={() => setAddExerciseOpen(true)}
                        disabled={busy}
                      >
                        <IconPlus size={16} stroke={2.5} />
                        Add exercise
                      </button>
                      <button
                        type="button"
                        className={classes.secondaryBtn}
                        onClick={() => setEndCustomConfirmOpen(true)}
                        disabled={busy || endingCustom}
                      >
                        End Workout
                      </button>
                    </div>
                  ) : null}
                </>
              )
            ) : isCustomSession ? (
              <div className={classes.secondaryRow}>
                <button
                  type="button"
                  className={classes.secondaryBtn}
                  onClick={() => setAddExerciseOpen(true)}
                  disabled={busy}
                >
                  <IconPlus size={16} stroke={2.5} />
                  Add exercise
                </button>
                <button
                  type="button"
                  className={classes.secondaryBtn}
                  onClick={() => setEndCustomConfirmOpen(true)}
                  disabled={busy || endingCustom}
                >
                  End Workout
                </button>
              </div>
            ) : (
              <div className={classes.secondaryRow}>
                <button
                  type="button"
                  className={classes.secondaryBtn}
                  onClick={() => {
                    clearSessionState();
                    setPhase(startable.length > 1 ? 'picker' : 'rest');
                  }}
                  disabled={busy || starting}
                >
                  Choose different workout
                </button>
              </div>
            )}
          </section>
          ) : (
            <section className={classes.card}>
              <p className={classes.exerciseEyebrow}>Today&apos;s Workout</p>
              <h2 className={classes.exerciseName}>Rest Day</h2>
              <p className={classes.demoText}>{restMessage}</p>
              <button
                type="button"
                className={classes.completeBtn}
                onClick={onBeginCustomFromIdle}
                disabled={busy}
              >
                <IconPlus size={18} stroke={2.5} />
                Custom Workout
              </button>
              <button
                type="button"
                className={classes.secondaryBtn}
                onClick={leaveWorkout}
                style={{ marginTop: '0.75rem', width: '100%' }}
              >
                Back to Home
              </button>
            </section>
          )}
        </div>

        <aside className={classes.rail}>
          {hasPrimedSession || isCustomEmpty ? (
            <>
          <section className={`${classes.card} ${classes.railQueueCard}`}>
            <p className={classes.railTitle}>Workout Exercises</p>
            {isCustomSession && !isCompleted ? (
              <button
                type="button"
                className={classes.secondaryBtn}
                onClick={() => setAddExerciseOpen(true)}
                disabled={busy || addingExercise}
                style={{ width: '100%', marginBottom: '0.75rem' }}
              >
                <IconPlus size={16} stroke={2.5} />
                Add exercise
              </button>
            ) : null}
            <ul className={classes.queueList}>
              {(session?.exercises || []).map((item, index) => {
                const status = exerciseStatus(item);
                const active = !isCompleted && index === exerciseIndex;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={[
                        classes.queueItem,
                        active ? classes.queueItemActive : '',
                        status === 'completed' || isCompleted
                          ? classes.queueItemDone
                          : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => {
                        if (!isCompleted) focusExercise(index);
                      }}
                      disabled={isCompleted}
                    >
                      <span
                        className={[
                          classes.queueMark,
                          status === 'completed' || isCompleted
                            ? classes.queueMarkDone
                            : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {status === 'completed' || isCompleted ? (
                          <IconCheck size={12} stroke={3} />
                        ) : (
                          index + 1
                        )}
                      </span>
                      <span className={classes.queueName}>{item.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>

          {isCompleted ? (
            <section className={classes.card}>
              <p className={classes.railTitle}>Session</p>
              <p className={classes.demoText}>All sets finished.</p>
              <button
                type="button"
                className={classes.completeBtn}
                onClick={leaveWorkout}
              >
                Back to Home
              </button>
            </section>
          ) : (
            <>
          <section className={`${classes.card} ${classes.railTimerCard}`}>
            <p className={classes.railTitle}>
              {showSetTimerRail ? 'Set Timer' : 'Rest Timer'}
            </p>
            <div className={classes.timerWrap}>
              {showRestTimerRail && restPhase === 'countdown' ? (
                <p className={classes.railCountdown}>{restSeconds || 0}</p>
              ) : (
                <div className={classes.timerRing}>
                  <svg className={classes.timerSvg} viewBox="0 0 140 140">
                    <circle
                      className={classes.timerTrack}
                      cx="70"
                      cy="70"
                      r="60"
                    />
                    <circle
                      className={
                        showSetTimerRail
                          ? classes.timerProgressSet
                          : classes.timerProgress
                      }
                      cx="70"
                      cy="70"
                      r="60"
                      strokeDasharray={ringCircumference}
                      strokeDashoffset={ringOffset}
                    />
                  </svg>
                  <div className={classes.timerValue}>
                    <p className={classes.timerClock}>
                      {formatClock(railSeconds)}
                    </p>
                    <p className={classes.timerCaption}>
                      {showSetTimerRail
                        ? 'Working'
                        : showRestTimerRail
                          ? 'Resting'
                          : 'Ready'}
                    </p>
                  </div>
                </div>
              )}
              {showRestTimerRail ? (
                <div className={classes.timerActions}>
                  <button
                    type="button"
                    className={classes.secondaryBtn}
                    onClick={() => setRestModalMode('expanded')}
                  >
                    Expand
                  </button>
                  <button
                    type="button"
                    className={classes.secondaryBtn}
                    onClick={() => onExtraRest(10)}
                    disabled={busy}
                  >
                    + 10 Sec
                  </button>
                  <button
                    type="button"
                    className={classes.skipRestBtn}
                    onClick={onSkipRest}
                    disabled={busy}
                  >
                    <IconPlayerSkipForward size={16} stroke={2.5} />
                    Skip Rest
                  </button>
                </div>
              ) : null}
              {hrStatus !== 'unsupported' ? (
                <div className={classes.railHr} aria-live="polite">
                  <p className={classes.railHrLabel}>Heart Rate</p>
                  <p className={classes.railHrValue}>
                    <HeartEkgIcon
                      size={28}
                      className={classes.hrIcon}
                      bpm={hrBpm}
                      active={hrStatus === 'connected'}
                    />
                    <span className={classes.railHrNumber}>
                      {hrStatus === 'connected' && hrBpm != null
                        ? hrBpm
                        : hrStatus === 'connecting'
                          ? '…'
                          : '—'}
                    </span>
                    <span className={classes.railHrUnit}>BPM</span>
                  </p>
                  {hrStatus !== 'connected' ? (
                    <p className={classes.railHrHint}>
                      {hrStatus === 'connecting'
                        ? 'Connecting strap…'
                        : 'Connect HR in the top bar'}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

          <section className={`${classes.card} ${classes.railNextCard}`}>
            <div className={classes.nextCard}>
              <div className={classes.nextThumb} />
              <div>
                <p className={classes.nextLabel}>Next Exercise</p>
                <p className={classes.nextName}>
                  {nextExercise ? nextExercise.name : 'Session complete'}
                </p>
                {nextExercise ? (
                  <p className={classes.nextRx}>
                    {prescriptionLabel(nextExercise)}
                  </p>
                ) : null}
              </div>
            </div>
          </section>
            </>
          )}
            </>
          ) : (
            <>
              <section className={classes.card}>
                <p className={classes.railTitle}>
                  {home?.calendar?.month_label || 'Calendar'}
                </p>
                <div className={homeClasses.calendarGrid}>
                  {DOW.map((label, index) => (
                    <div key={`${label}-${index}`} className={homeClasses.calDow}>
                      {label}
                    </div>
                  ))}
                  {calendarCells.map((cell) =>
                    cell.muted ? (
                      <div
                        key={cell.key}
                        className={`${homeClasses.calDay} ${homeClasses.calDayMuted}`}
                      />
                    ) : (
                      <button
                        key={cell.key}
                        type="button"
                        className={[
                          homeClasses.calDay,
                          homeClasses.calDayButton,
                          cell.workout ? homeClasses.calDayWorkout : '',
                          cell.overlay && !cell.workout
                            ? homeClasses.calDayOverlay
                            : '',
                          cell.overlay && cell.workout
                            ? homeClasses.calDayBoth
                            : '',
                          cell.today ? homeClasses.calDayToday : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => openDayModal(cell.day)}
                        aria-label={`Open ${cell.label}`}
                      >
                        <span className={homeClasses.calDayNum}>{cell.label}</span>
                        {cell.overlay ? (
                          <span
                            className={homeClasses.calOverlayDot}
                            aria-hidden
                          />
                        ) : null}
                      </button>
                    ),
                  )}
                </div>
              </section>

              <section className={classes.card}>
                <p className={classes.railTitle}>This Week</p>
                <ul className={homeClasses.weekList}>
                  {(home?.this_week || []).map((row) => (
                    <li
                      key={row.day}
                      className={`${homeClasses.weekRow}${
                        row.status === 'today' ? ` ${homeClasses.weekToday}` : ''
                      }`}
                    >
                      <span
                        className={[
                          homeClasses.weekMark,
                          row.status === 'completed' ? homeClasses.weekDone : '',
                          row.status === 'today' ? homeClasses.weekNow : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {row.status === 'completed' ? (
                          <IconCheck size={12} stroke={3} />
                        ) : null}
                      </span>
                      <span className={homeClasses.weekDay}>{row.day}</span>
                      <span className={homeClasses.weekLabel}>{row.label}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className={classes.card}>
                <p className={classes.railTitle}>Latest Activity</p>
                <ul className={homeClasses.activityList}>
                  {(home?.latest_activity || []).map((item) => (
                    <li
                      key={`${item.date_label}-${item.name}`}
                      className={homeClasses.activityRow}
                    >
                      <span className={homeClasses.activityDate}>
                        {item.date_label}
                      </span>
                      <span className={homeClasses.activityName}>{item.name}</span>
                      <IconCheck
                        size={16}
                        className={homeClasses.check}
                        stroke={2.5}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </aside>
      </div>

      <div className={classes.footerStats}>
        <div className={classes.footerCard}>
          <div className={classes.footerTop}>
            <p className={classes.footerLabel}>Workout Volume</p>
            <IconChartBar size={16} stroke={1.75} />
          </div>
          <p className={classes.footerValue}>{volume.toLocaleString()} LB</p>
        </div>
        <div className={classes.footerCard}>
          <div className={classes.footerTop}>
            <p className={classes.footerLabel}>Time Elapsed</p>
            <IconClock size={16} stroke={1.75} />
          </div>
          <p className={classes.footerValue}>{formatClock(elapsed)}</p>
        </div>
        <div className={classes.footerCard}>
          <div className={classes.footerTop}>
            <p className={classes.footerLabel}>Est. Time Remaining</p>
            <IconHourglass size={16} stroke={1.75} />
          </div>
          <p className={classes.footerValue}>{formatClock(remaining)}</p>
        </div>
      </div>

      {skipSetConfirmOpen ? (
        <div
          className={classes.confirmOverlay}
          role="presentation"
          onClick={() => !busy && setSkipSetConfirmOpen(false)}
        >
          <div
            className={classes.confirmDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="skip-set-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="skip-set-title" className={classes.confirmTitle}>
              Skip this set?
            </p>
            <p className={classes.confirmBody}>
              This set will be marked skipped and you&apos;ll move on.
            </p>
            <div className={classes.confirmActions}>
              <button
                type="button"
                className={classes.confirmCancel}
                onClick={() => setSkipSetConfirmOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={classes.confirmDanger}
                onClick={onSkipSet}
                disabled={busy}
              >
                {busy ? 'Skipping…' : 'Skip Set'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {unskipSetConfirmOpen ? (
        <div
          className={classes.confirmOverlay}
          role="presentation"
          onClick={() => !busy && setUnskipSetConfirmOpen(false)}
        >
          <div
            className={classes.confirmDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="unskip-set-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="unskip-set-title" className={classes.confirmTitle}>
              Undo skip?
            </p>
            <p className={classes.confirmBody}>
              This set will be open again so you can complete it.
            </p>
            <div className={classes.confirmActions}>
              <button
                type="button"
                className={classes.confirmCancel}
                onClick={() => setUnskipSetConfirmOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={classes.confirmDanger}
                onClick={onUnskipSet}
                disabled={busy}
              >
                {busy ? 'Undoing…' : 'Undo Skip'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {completeModalOpen ? (
        <div
          className={classes.confirmOverlay}
          role="presentation"
          onClick={() => !busy && cancelCompleteModal()}
        >
          <div
            className={classes.confirmDialogWide}
            role="dialog"
            aria-modal="true"
            aria-labelledby="complete-set-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="complete-set-title" className={classes.confirmTitle}>
              Submit this set?
            </p>
            <p className={classes.confirmBody}>
              Adjust the values if needed, then submit.
            </p>
            <p className={classes.modalAdjustmentsLabel}>My Adjustments</p>
            <div className={classes.steppers}>
              <div className={classes.stepper}>
                <p className={classes.stepperLabel}>Weight (lb)</p>
                <div className={classes.stepperControls}>
                  <input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step={1}
                    className={classes.stepValueInput}
                    value={weight > 0 ? weight : ''}
                    placeholder="—"
                    disabled={busy}
                    aria-label="Weight in pounds"
                    onFocus={(event) => event.target.select()}
                    onChange={(event) => {
                      const raw = event.target.value;
                      if (raw === '') {
                        setWeight(0);
                        return;
                      }
                      const next = Number(raw);
                      if (!Number.isFinite(next) || next < 0) return;
                      setWeight(toWholeLb(next) ?? 0);
                    }}
                  />
                </div>
              </div>
              <div className={classes.stepper}>
                <p className={classes.stepperLabel}>Reps</p>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={busy}
                    onClick={() => setReps((value) => Math.max(0, value - 1))}
                  >
                    −
                  </button>
                  <span className={classes.stepValue}>{reps || '—'}</span>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={busy}
                    onClick={() => setReps((value) => value + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className={classes.stepper}>
                <p className={classes.stepperLabel}>RPE</p>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={busy}
                    onClick={() => setRpe((value) => Math.max(1, value - 1))}
                  >
                    −
                  </button>
                  <span className={classes.stepValue}>{rpe}</span>
                  <button
                    type="button"
                    className={classes.stepBtn}
                    disabled={busy}
                    onClick={() => setRpe((value) => Math.min(10, value + 1))}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <div className={classes.confirmActions}>
              <button
                type="button"
                className={classes.confirmCancel}
                onClick={cancelCompleteModal}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className={classes.confirmSubmit}
                onClick={onSubmitSetFromModal}
                disabled={busy}
              >
                {busy ? 'Submitting…' : 'Submit Set'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRestModal ? (
        <div
          className={classes.restOverlay}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rest-timer-title"
        >
          <div
            className={[
              classes.restDialog,
              restEndingFlash ? classes.restDialogFlash : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {restEndingFlash ? (
              <button
                type="button"
                className={classes.restGoDismiss}
                onClick={dismissRestGo}
                aria-label="I'm ready — dismiss and start next set"
              >
                <span className={classes.restFlashMessage}>Go</span>
                <span className={classes.restGoHint}>Tap when ready</span>
              </button>
            ) : (
              <>
                <header className={classes.restHeader}>
                  <div>
                    <p id="rest-timer-title" className={classes.restTitle}>
                      Rest Timer
                    </p>
                    <p className={classes.restSubtitle}>
                      Recover now. Perform next.
                    </p>
                  </div>
                  <button
                    type="button"
                    className={classes.restMinimizeBtn}
                    onClick={() => setRestModalMode('minimized')}
                    aria-label="Minimize rest timer"
                  >
                    <IconMinimize size={18} stroke={2.5} />
                    Minimize
                  </button>
                </header>

                <div className={classes.restContext}>
                  <p className={classes.restExercise}>
                    {restModalContext?.exerciseName ||
                      exercise?.name ||
                      'Exercise'}
                  </p>
                  <p className={classes.restSetMeta}>
                    {restModalContext?.setMeta || ''}
                  </p>
                </div>

                {restPhase === 'countdown' ? (
                  <div className={classes.restCountdownWrap}>
                    <p className={classes.restCountdown}>{restSeconds}</p>
                  </div>
                ) : (
                  <div className={classes.restRingWrap}>
                    <div className={classes.restRing}>
                      <svg className={classes.restRingSvg} viewBox="0 0 200 200">
                        <circle
                          className={classes.restRingTrack}
                          cx="100"
                          cy="100"
                          r="86"
                        />
                        <circle
                          className={classes.restRingProgress}
                          cx="100"
                          cy="100"
                          r="86"
                          strokeDasharray={2 * Math.PI * 86}
                          strokeDashoffset={
                            2 *
                            Math.PI *
                            86 *
                            (1 - (restTotal ? restSeconds / restTotal : 0))
                          }
                        />
                      </svg>
                      <div className={classes.restRingValue}>
                        <IconHourglass size={22} stroke={2} />
                        <p className={classes.restRingClock}>
                          {formatClock(restSeconds)}
                        </p>
                        <p className={classes.restRingTotal}>
                          / {formatClock(restTotal)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className={classes.restMetrics}>
                  {hrStatus !== 'unsupported' ? (
                    <div className={classes.restMetricCardHr} aria-live="polite">
                      <p className={classes.restMetricLabel}>Heart Rate</p>
                      <p className={classes.restHrValue}>
                        <HeartEkgIcon
                          size={36}
                          className={classes.hrIcon}
                          bpm={hrBpm}
                          active={hrStatus === 'connected'}
                        />
                        <span className={classes.restHrNumber}>
                          {hrStatus === 'connected' && hrBpm != null
                            ? hrBpm
                            : hrStatus === 'connecting'
                              ? '…'
                              : '—'}
                        </span>
                        <span className={classes.restHrUnit}>BPM</span>
                      </p>
                      {hrStatus !== 'connected' ? (
                        <p className={classes.restMetricSub}>
                          {hrStatus === 'connecting'
                            ? 'Connecting…'
                            : 'Connect HR in the top bar'}
                        </p>
                      ) : null}
                    </div>
                  ) : null}
                  <div className={classes.restMetricCard}>
                    <p className={classes.restMetricLabel}>Next</p>
                    <p className={classes.restMetricValue}>
                      {restModalContext?.nextLabel || 'Next set'}
                    </p>
                    {restModalContext?.nextSub ? (
                      <p className={classes.restMetricSub}>
                        {restModalContext.nextSub}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className={classes.restControls}>
                  <div className={classes.restControlRow}>
                    <button
                      type="button"
                      className={classes.restAdjustBtn}
                      onClick={() => onExtraRest(-10)}
                      disabled={busy || restSeconds <= 0}
                    >
                      −10s
                    </button>
                    <button
                      type="button"
                      className={classes.restAdjustBtn}
                      onClick={() => onExtraRest(10)}
                      disabled={busy}
                    >
                      +10s
                    </button>
                  </div>
                  <button
                    type="button"
                    className={classes.restSkipLink}
                    onClick={onSkipRest}
                    disabled={busy}
                  >
                    <IconPlayerSkipForward size={16} stroke={2.5} />
                    Skip Rest
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      <AddExerciseSheet
        opened={addExerciseOpen}
        onClose={() => !addingExercise && setAddExerciseOpen(false)}
        onConfirm={onConfirmAddExercise}
        confirming={addingExercise}
      />

      {endCustomConfirmOpen ? (
        <div
          className={classes.confirmOverlay}
          role="presentation"
          onClick={() => !endingCustom && setEndCustomConfirmOpen(false)}
        >
          <div
            className={classes.confirmDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="end-custom-title"
            onClick={(event) => event.stopPropagation()}
          >
            <p id="end-custom-title" className={classes.confirmTitle}>
              End custom workout?
            </p>
            <p className={classes.confirmBody}>
              Your session will be saved. Today&apos;s suggested workout will be
              skipped if it is still pending.
            </p>
            <div className={classes.confirmActions}>
              <button
                type="button"
                className={classes.confirmCancel}
                onClick={() => setEndCustomConfirmOpen(false)}
                disabled={endingCustom}
              >
                Keep going
              </button>
              <button
                type="button"
                className={classes.confirmDanger}
                onClick={onEndCustomWorkout}
                disabled={endingCustom}
              >
                {endingCustom ? 'Ending…' : 'End Workout'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {customContinueOpen ? (
        <div className={classes.confirmOverlay} role="presentation">
          <div
            className={classes.confirmDialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="custom-continue-title"
          >
            <p id="custom-continue-title" className={classes.confirmTitle}>
              Sets finished
            </p>
            <p className={classes.confirmBody}>
              Add another exercise, or end the workout to save it.
            </p>
            <div className={classes.confirmActions}>
              <button
                type="button"
                className={classes.confirmCancel}
                onClick={() => {
                  setCustomContinueOpen(false);
                  setAddExerciseOpen(true);
                }}
                disabled={busy || endingCustom}
              >
                Add exercise
              </button>
              <button
                type="button"
                className={classes.confirmDanger}
                onClick={() => {
                  setCustomContinueOpen(false);
                  setEndCustomConfirmOpen(true);
                }}
                disabled={busy || endingCustom}
              >
                End Workout
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <DayActivityModal
        opened={Boolean(modalDate)}
        onClose={closeDayModal}
        dateLabel={modalDateLabel}
        entries={dayEntries}
        loading={modalLoading}
        isToday={modalDateKey === todayKey}
        onLoadWorkout={(target) => {
          closeDayModal();
          setBusy(true);
          loadStartableItem({
            source: target.source,
            workout_slot_id: target.source === 'slot' ? target.id : null,
            athlete_calendar_event_id:
              target.source === 'overlay' ? target.id : null,
            on_date: target.onDate,
          })
            .catch((error) => toast.error(toErrorMessage(error)))
            .finally(() => setBusy(false));
        }}
      />

    </div>
  );
}
